import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase';

/** Firestore integrations doc for multi-LLM BYOK settings. */
export const AI_PROVIDER_INTEGRATION_DOC_ID = 'ai_provider';

/** Legacy Gemini BYOK doc — kept in sync for existing Cloud Function readers. */
export const GEMINI_INTEGRATION_DOC_ID = 'gemini';

export const AI_PROVIDER_IDS = {
  GOOGLE: 'google',
  ANTHROPIC: 'anthropic',
  DEEPSEEK: 'deepseek',
  LOCAL: 'local',
};

const PROVIDER_META = [
  {
    id: AI_PROVIDER_IDS.GOOGLE,
    label: 'Google Gemini',
    description: 'Google AI Studio — used for agents, Scrum Master, and Game Studio manifests.',
    placeholder: 'AIza…',
    Icon: AutoAwesomeOutlinedIcon,
  },
  {
    id: AI_PROVIDER_IDS.ANTHROPIC,
    label: 'Anthropic Claude',
    description: 'Claude API — available as an alternate LLM for agent orchestration.',
    placeholder: 'sk-ant-…',
    Icon: PsychologyOutlinedIcon,
  },
  {
    id: AI_PROVIDER_IDS.DEEPSEEK,
    label: 'DeepSeek',
    description:
      'DeepSeek API (OpenAI-compatible) — select deepseek-v4-flash or deepseek-v4-pro per agent. Host is fixed to api.deepseek.com.',
    placeholder: 'sk-…',
    Icon: SmartToyOutlinedIcon,
  },
];

/**
 * @param {string} providerId
 * @returns {string}
 */
function providerKeyLabel(providerId) {
  if (providerId === AI_PROVIDER_IDS.ANTHROPIC) return 'Anthropic Claude';
  if (providerId === AI_PROVIDER_IDS.DEEPSEEK) return 'DeepSeek';
  return 'Google Gemini';
}

/**
 * @param {string} providerId
 * @returns {string}
 */
function providerShortLabel(providerId) {
  if (providerId === AI_PROVIDER_IDS.ANTHROPIC) return 'Anthropic';
  if (providerId === AI_PROVIDER_IDS.DEEPSEEK) return 'DeepSeek';
  return 'Google';
}

/**
 * @param {string | null | undefined} key
 * @returns {string}
 */
function maskByokApiKey(key) {
  if (!key || typeof key !== 'string') return '';
  const trimmed = key.trim();
  if (!trimmed) return '';
  if (trimmed.length <= 4) return '••••';
  return `${'•'.repeat(8)}${trimmed.slice(-4)}`;
}

/**
 * @param {string} input
 * @param {string} storedFull
 * @returns {boolean}
 */
function isMaskedByokPlaceholder(input, storedFull) {
  if (!input || !storedFull) return false;
  return input === maskByokApiKey(storedFull) || /^•+\S{0,4}$/.test(input.trim());
}

/**
 * @param {unknown} value
 * @returns {'google' | 'anthropic'}
 */
function normalizeActiveProvider(value) {
  return value === AI_PROVIDER_IDS.ANTHROPIC
    ? AI_PROVIDER_IDS.ANTHROPIC
    : AI_PROVIDER_IDS.GOOGLE;
}

/**
 * @param {object|null|undefined} data
 * @returns {{ apiKey: string, configured: boolean }}
 */
function readProviderSlot(data) {
  if (!data || typeof data !== 'object') {
    return { apiKey: '', configured: false };
  }
  const apiKey = typeof data.apiKey === 'string' ? data.apiKey.trim() : '';
  return {
    apiKey,
    configured: data.configured === true || Boolean(apiKey),
  };
}

/**
 * @param {object|null|undefined} data
 * @returns {{
 *   baseUrl: string,
 *   modelName: string,
 *   apiKey: string,
 *   enabled: boolean,
 *   configured: boolean,
 * }}
 */
function readLocalSlot(data) {
  if (!data || typeof data !== 'object') {
    return {
      baseUrl: '',
      modelName: '',
      apiKey: '',
      enabled: false,
      configured: false,
    };
  }
  const baseUrl = typeof data.baseUrl === 'string' ? data.baseUrl.trim() : '';
  const modelName = typeof data.modelName === 'string' ? data.modelName.trim() : '';
  const apiKey = typeof data.apiKey === 'string' ? data.apiKey.trim() : '';
  const enabled = data.enabled === true;
  return {
    baseUrl,
    modelName,
    apiKey,
    enabled,
    configured: data.configured === true || Boolean(baseUrl),
  };
}

/**
 * Multi-LLM BYOK settings: active provider switch + per-provider API keys
 * + OpenAI-compatible local endpoint (SSRF-validated callable).
 * Persists to companies/{companyId}/integrations/ai_provider.
 *
 * @param {{ companyId: string | null | undefined }} props
 */
export default function MultiProviderConfig({ companyId }) {
  const resolvedCompanyId =
    typeof companyId === 'string' ? companyId.trim() : '';

  const [loading, setLoading] = useState(false);
  const [activeProvider, setActiveProvider] = useState(AI_PROVIDER_IDS.GOOGLE);
  const [savingActive, setSavingActive] = useState(false);

  const [keysDraft, setKeysDraft] = useState({
    google: '',
    anthropic: '',
    deepseek: '',
  });
  const [keysStored, setKeysStored] = useState({
    google: '',
    anthropic: '',
    deepseek: '',
  });
  const [configured, setConfigured] = useState({
    google: false,
    anthropic: false,
    deepseek: false,
  });
  const [showKey, setShowKey] = useState({
    google: false,
    anthropic: false,
    deepseek: false,
    local: false,
  });
  const [savingKey, setSavingKey] = useState({
    google: false,
    anthropic: false,
    deepseek: false,
  });

  const [localBaseUrl, setLocalBaseUrl] = useState('');
  const [localModelName, setLocalModelName] = useState('');
  const [localApiKeyDraft, setLocalApiKeyDraft] = useState('');
  const [localApiKeyStored, setLocalApiKeyStored] = useState('');
  const [localEnabled, setLocalEnabled] = useState(false);
  const [localConfigured, setLocalConfigured] = useState(false);
  const [savingLocal, setSavingLocal] = useState(false);
  const [removingLocal, setRemovingLocal] = useState(false);
  const [removingKey, setRemovingKey] = useState({
    google: false,
    anthropic: false,
    deepseek: false,
  });

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadConfig = useCallback(async () => {
    if (!resolvedCompanyId) return;
    setLoading(true);
    setError('');
    try {
      const [aiSnap, geminiSnap] = await Promise.all([
        getDoc(
          doc(
            db,
            'companies',
            resolvedCompanyId,
            'integrations',
            AI_PROVIDER_INTEGRATION_DOC_ID,
          ),
        ),
        getDoc(
          doc(
            db,
            'companies',
            resolvedCompanyId,
            'integrations',
            GEMINI_INTEGRATION_DOC_ID,
          ),
        ),
      ]);

      const aiData = aiSnap.exists() ? aiSnap.data() || {} : {};
      const providers =
        aiData.providers && typeof aiData.providers === 'object'
          ? aiData.providers
          : {};

      let google = readProviderSlot(providers.google);
      const anthropic = readProviderSlot(providers.anthropic);
      const deepseek = readProviderSlot(providers.deepseek);
      const local = readLocalSlot(providers.local);

      // Migrate legacy integrations/gemini into the multi-provider model.
      if (!google.apiKey && geminiSnap.exists()) {
        const legacyKey =
          typeof geminiSnap.data()?.apiKey === 'string'
            ? geminiSnap.data().apiKey.trim()
            : '';
        if (legacyKey) {
          google = { apiKey: legacyKey, configured: true };
        }
      }

      setActiveProvider(normalizeActiveProvider(aiData.activeProvider));
      setKeysStored({
        google: google.apiKey,
        anthropic: anthropic.apiKey,
        deepseek: deepseek.apiKey,
      });
      setKeysDraft({
        google: google.apiKey ? maskByokApiKey(google.apiKey) : '',
        anthropic: anthropic.apiKey ? maskByokApiKey(anthropic.apiKey) : '',
        deepseek: deepseek.apiKey ? maskByokApiKey(deepseek.apiKey) : '',
      });
      setConfigured({
        google: google.configured,
        anthropic: anthropic.configured,
        deepseek: deepseek.configured,
      });

      setLocalBaseUrl(local.baseUrl);
      setLocalModelName(local.modelName);
      setLocalApiKeyStored(local.apiKey);
      setLocalApiKeyDraft(local.apiKey ? maskByokApiKey(local.apiKey) : '');
      setLocalEnabled(local.enabled);
      setLocalConfigured(local.configured);
    } catch (err) {
      console.error('MultiProviderConfig load:', err);
      setError(err?.message || 'Could not load AI provider settings.');
    } finally {
      setLoading(false);
    }
  }, [resolvedCompanyId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleActiveProviderChange = async (event) => {
    const next = normalizeActiveProvider(event.target.value);
    setActiveProvider(next);
    if (!resolvedCompanyId) return;

    setSavingActive(true);
    setError('');
    setMessage('');
    try {
      await setDoc(
        doc(
          db,
          'companies',
          resolvedCompanyId,
          'integrations',
          AI_PROVIDER_INTEGRATION_DOC_ID,
        ),
        {
          activeProvider: next,
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
        { merge: true },
      );
      setMessage(
        next === AI_PROVIDER_IDS.ANTHROPIC
          ? 'Active AI provider set to Anthropic Claude.'
          : 'Active AI provider set to Google Gemini.',
      );
    } catch (err) {
      console.error('MultiProviderConfig save activeProvider:', err);
      setError(err?.message || 'Could not update active provider.');
    } finally {
      setSavingActive(false);
    }
  };

  const handleSaveProviderKey = async (providerId) => {
    if (!resolvedCompanyId) return;
    const typed = (keysDraft[providerId] || '').trim();
    const stored = keysStored[providerId] || '';
    const nextKey = isMaskedByokPlaceholder(typed, stored)
      ? stored.trim()
      : typed;

    if (!nextKey) {
      setError(`Enter an API key for ${providerShortLabel(providerId)}.`);
      return;
    }

    setSavingKey((prev) => ({ ...prev, [providerId]: true }));
    setError('');
    setMessage('');
    try {
      const providerPayload = {
        apiKey: nextKey,
        configured: true,
        updatedAt: serverTimestamp(),
      };

      const aiRef = doc(
        db,
        'companies',
        resolvedCompanyId,
        'integrations',
        AI_PROVIDER_INTEGRATION_DOC_ID,
      );
      const existingSnap = await getDoc(aiRef);
      const existing = existingSnap.exists() ? existingSnap.data() || {} : {};
      const existingProviders =
        existing.providers && typeof existing.providers === 'object'
          ? existing.providers
          : {};

      await setDoc(
        aiRef,
        {
          activeProvider,
          providers: {
            ...existingProviders,
            [providerId]: providerPayload,
          },
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
        { merge: true },
      );

      // Keep legacy Gemini doc in sync for existing Cloud Functions.
      if (providerId === AI_PROVIDER_IDS.GOOGLE) {
        await setDoc(
          doc(
            db,
            'companies',
            resolvedCompanyId,
            'integrations',
            GEMINI_INTEGRATION_DOC_ID,
          ),
          {
            apiKey: nextKey,
            updatedAt: serverTimestamp(),
            updatedByUid: auth.currentUser?.uid || null,
          },
          { merge: true },
        );
      }

      setKeysStored((prev) => ({ ...prev, [providerId]: nextKey }));
      setKeysDraft((prev) => ({
        ...prev,
        [providerId]: maskByokApiKey(nextKey),
      }));
      setConfigured((prev) => ({ ...prev, [providerId]: true }));
      setMessage(`${providerKeyLabel(providerId)} API key saved.`);
    } catch (err) {
      console.error('MultiProviderConfig save key:', err);
      setError(err?.message || 'Could not save API key.');
    } finally {
      setSavingKey((prev) => ({ ...prev, [providerId]: false }));
    }
  };

  const handleClearProviderKey = async (providerId) => {
    if (!resolvedCompanyId) return;
    const label = providerKeyLabel(providerId);
    if (
      !window.confirm(
        `Remove the ${label} API key from this workspace? Agents that need it will fail until you save a new key.`,
      )
    ) {
      return;
    }

    setRemovingKey((prev) => ({ ...prev, [providerId]: true }));
    setError('');
    setMessage('');
    try {
      const aiRef = doc(
        db,
        'companies',
        resolvedCompanyId,
        'integrations',
        AI_PROVIDER_INTEGRATION_DOC_ID,
      );
      const existingSnap = await getDoc(aiRef);
      const existing = existingSnap.exists() ? existingSnap.data() || {} : {};
      const existingProviders =
        existing.providers && typeof existing.providers === 'object'
          ? existing.providers
          : {};

      await setDoc(
        aiRef,
        {
          providers: {
            ...existingProviders,
            [providerId]: {
              apiKey: '',
              configured: false,
              updatedAt: serverTimestamp(),
            },
          },
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
        { merge: true },
      );

      if (providerId === AI_PROVIDER_IDS.GOOGLE) {
        await setDoc(
          doc(
            db,
            'companies',
            resolvedCompanyId,
            'integrations',
            GEMINI_INTEGRATION_DOC_ID,
          ),
          {
            apiKey: '',
            updatedAt: serverTimestamp(),
            updatedByUid: auth.currentUser?.uid || null,
          },
          { merge: true },
        );
      }

      setKeysStored((prev) => ({ ...prev, [providerId]: '' }));
      setKeysDraft((prev) => ({ ...prev, [providerId]: '' }));
      setConfigured((prev) => ({ ...prev, [providerId]: false }));
      setMessage(`${label} API key removed.`);
    } catch (err) {
      console.error('MultiProviderConfig clear key:', err);
      setError(err?.message || 'Could not remove API key.');
    } finally {
      setRemovingKey((prev) => ({ ...prev, [providerId]: false }));
    }
  };

  const handleSaveLocal = async () => {
    if (!resolvedCompanyId) return;
    setSavingLocal(true);
    setError('');
    setMessage('');
    try {
      const typed = (localApiKeyDraft || '').trim();
      const nextKey = isMaskedByokPlaceholder(typed, localApiKeyStored)
        ? localApiKeyStored.trim()
        : typed;

      const fn = httpsCallable(functions, 'saveCompanyLocalModelProvider');
      const result = await fn({
        companyId: resolvedCompanyId,
        baseUrl: localBaseUrl.trim(),
        modelName: localModelName.trim(),
        apiKey: nextKey,
        enabled: localEnabled === true,
      });
      const payload = result?.data || {};
      if (payload.baseUrl) setLocalBaseUrl(payload.baseUrl);
      if (payload.modelName) setLocalModelName(payload.modelName);
      if (typeof payload.enabled === 'boolean') setLocalEnabled(payload.enabled);
      if (nextKey) {
        setLocalApiKeyStored(nextKey);
        setLocalApiKeyDraft(maskByokApiKey(nextKey));
      } else {
        setLocalApiKeyStored('');
        setLocalApiKeyDraft('');
      }
      setLocalConfigured(true);
      setMessage(
        payload.enabled
          ? 'Local model endpoint saved and enabled.'
          : 'Local model endpoint saved (disabled until you enable it).',
      );
    } catch (err) {
      console.error('MultiProviderConfig save local:', err);
      setError(
        err?.message ||
          err?.details ||
          'Could not save local model provider.',
      );
    } finally {
      setSavingLocal(false);
    }
  };

  const handleClearLocal = async () => {
    if (!resolvedCompanyId) return;
    if (
      !window.confirm(
        'Remove the company local model configuration (URL, model name, and API key)? Agents with local:… targets will fail until you configure again.',
      )
    ) {
      return;
    }

    setRemovingLocal(true);
    setError('');
    setMessage('');
    try {
      const fn = httpsCallable(functions, 'saveCompanyLocalModelProvider');
      await fn({
        companyId: resolvedCompanyId,
        clear: true,
      });
      setLocalBaseUrl('');
      setLocalModelName('');
      setLocalApiKeyStored('');
      setLocalApiKeyDraft('');
      setLocalEnabled(false);
      setLocalConfigured(false);
      setMessage('Local model configuration removed.');
    } catch (err) {
      console.error('MultiProviderConfig clear local:', err);
      setError(
        err?.message ||
          err?.details ||
          'Could not remove local model configuration.',
      );
    } finally {
      setRemovingLocal(false);
    }
  };

  if (!resolvedCompanyId) {
    return (
      <Alert severity="info">
        Select a company workspace to configure AI providers.
      </Alert>
    );
  }

  const localConnected =
    localConfigured || (localEnabled && Boolean(localBaseUrl.trim()));

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          AI providers (BYOK)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect Google Gemini, Anthropic Claude, DeepSeek, and/or an
          OpenAI-compatible local endpoint. Choose which cloud provider agents
          use by default (Gemini/Claude); DeepSeek and local models are
          selected per agent via model target.
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {message ? (
        <Alert severity="success" onClose={() => setMessage('')}>
          {message}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Card variant="outlined">
            <CardContent>
              <FormControl component="fieldset" disabled={savingActive}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                  Active provider
                </FormLabel>
                <RadioGroup
                  row
                  value={activeProvider}
                  onChange={handleActiveProviderChange}
                >
                  <FormControlLabel
                    value={AI_PROVIDER_IDS.GOOGLE}
                    control={<Radio />}
                    label="Google Gemini"
                  />
                  <FormControlLabel
                    value={AI_PROVIDER_IDS.ANTHROPIC}
                    control={<Radio />}
                    label="Anthropic Claude"
                  />
                </RadioGroup>
              </FormControl>
              {savingActive ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Saving…
                  </Typography>
                </Box>
              ) : null}
            </CardContent>
          </Card>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="stretch"
          >
            {PROVIDER_META.map((meta) => {
              const { id, label, description, placeholder, Icon } = meta;
              const isActive = activeProvider === id;
              const isConfigured = configured[id];
              const isSaving = savingKey[id];
              const isRemoving = removingKey[id];

              return (
                <Card
                  key={id}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    borderColor: isActive ? 'primary.main' : 'divider',
                    boxShadow: isActive ? 1 : 0,
                  }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon color="primary" />
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {description}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {isActive ? (
                            <Chip size="small" color="primary" label="Active" />
                          ) : null}
                          <Chip
                            size="small"
                            color={isConfigured ? 'success' : 'default'}
                            variant={isConfigured ? 'filled' : 'outlined'}
                            label={isConfigured ? 'Connected' : 'Not connected'}
                          />
                        </Stack>
                      </Stack>

                      <TextField
                        label={`${label} API key`}
                        type={showKey[id] ? 'text' : 'password'}
                        value={keysDraft[id]}
                        onChange={(e) =>
                          setKeysDraft((prev) => ({
                            ...prev,
                            [id]: e.target.value,
                          }))
                        }
                        onFocus={() => {
                          if (isMaskedByokPlaceholder(keysDraft[id], keysStored[id])) {
                            setKeysDraft((prev) => ({ ...prev, [id]: '' }));
                          }
                        }}
                        fullWidth
                        autoComplete="off"
                        placeholder={placeholder}
                        helperText="Existing keys are masked until you replace them."
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setShowKey((prev) => ({
                                    ...prev,
                                    [id]: !prev[id],
                                  }))
                                }
                                aria-label={
                                  showKey[id]
                                    ? `Hide ${label} API key`
                                    : `Show ${label} API key`
                                }
                                edge="end"
                              >
                                {showKey[id] ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={
                            isSaving ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          onClick={() => handleSaveProviderKey(id)}
                          disabled={isSaving || isRemoving}
                        >
                          Save {label} key
                        </Button>
                        {isConfigured ? (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={
                              isRemoving ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <DeleteOutlineIcon />
                              )
                            }
                            onClick={() => handleClearProviderKey(id)}
                            disabled={isSaving || isRemoving}
                          >
                            Remove key
                          </Button>
                        ) : null}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MemoryOutlinedIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Local model (OpenAI-compatible)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Company BYOK for LocalAI, vLLM, or any OpenAI-compatible
                        server via a public HTTPS tunnel. Hire agents with model
                        target <code>local:default</code>.
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    size="small"
                    color={localConnected ? 'success' : 'default'}
                    variant={localConnected ? 'filled' : 'outlined'}
                    label={localConnected ? 'Connected' : 'Not connected'}
                  />
                </Stack>

                <TextField
                  label="Base URL (HTTPS tunnel)"
                  value={localBaseUrl}
                  onChange={(e) => setLocalBaseUrl(e.target.value)}
                  fullWidth
                  autoComplete="off"
                  placeholder="https://….ngrok-free.app"
                  helperText="Public https:// only. Edit and save to correct a wrong URL, or use Remove configuration to reset."
                />

                <TextField
                  label="Model name"
                  value={localModelName}
                  onChange={(e) => setLocalModelName(e.target.value)}
                  fullWidth
                  autoComplete="off"
                  placeholder="Exact server model id"
                  helperText="Must match the server’s loaded model id exactly (vLLM is strict)."
                />

                <TextField
                  label="API key (optional)"
                  type={showKey.local ? 'text' : 'password'}
                  value={localApiKeyDraft}
                  onChange={(e) => setLocalApiKeyDraft(e.target.value)}
                  onFocus={() => {
                    if (isMaskedByokPlaceholder(localApiKeyDraft, localApiKeyStored)) {
                      setLocalApiKeyDraft('');
                    }
                  }}
                  fullWidth
                  autoComplete="off"
                  helperText="Sent as Bearer token when set. Clear the field and save to remove the key."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowKey((prev) => ({
                              ...prev,
                              local: !prev.local,
                            }))
                          }
                          aria-label={
                            showKey.local ? 'Hide local API key' : 'Show local API key'
                          }
                          edge="end"
                        >
                          {showKey.local ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={localEnabled}
                      onChange={(e) => setLocalEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Enable company local model"
                />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={
                      savingLocal ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    onClick={handleSaveLocal}
                    disabled={savingLocal || removingLocal}
                  >
                    Save local model
                  </Button>
                  {localConfigured ? (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={
                        removingLocal ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DeleteOutlineIcon />
                        )
                      }
                      onClick={handleClearLocal}
                      disabled={savingLocal || removingLocal}
                    >
                      Remove configuration
                    </Button>
                  ) : null}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
